import React, { useEffect, useState, useMemo } from 'react';
import nremtUnresponsiveLayout from '../data/nremtBasicAssessment';
import nremtResponsiveLayout from '../data/nremtResponsiveAssessment';
import { db } from '../firebase';
import { collection, addDoc, getDocs, doc, deleteDoc, query, orderBy } from 'firebase/firestore';

// Sample scenarios for unresponsive patients
const UNRESPONSIVE_SCENARIOS = [
  {
    id: 'adult-unresponsive-trauma',
    title: 'Adult Unresponsive Trauma - High Speed MVC',
    file: '/scenarios/adult-unresponsive-trauma.json',
    formType: 'unresponsive'
  },
  {
    id: 'adult-unresponsive-trauma-2',
    title: 'Adult Unresponsive Trauma - Fall From Height',
    file: '/scenarios/adult-unresponsive-trauma-2.json',
    formType: 'unresponsive'
  }
];

// Sample scenarios for responsive patients
const RESPONSIVE_SCENARIOS = [
  {
    id: 'adult-responsive-sob',
    title: 'Adult Responsive - Shortness of Breath / Hypoxia',
    file: '/scenarios/adult-responsive-sob.json',
    formType: 'responsive'
  }
];

// All sample scenarios combined
const ALL_SAMPLE_SCENARIOS = [...UNRESPONSIVE_SCENARIOS, ...RESPONSIVE_SCENARIOS];

const buildInitialStateMap = (rows, key) => {
  const initial = {};
  rows.forEach((row) => {
    if (row[key]) {
      initial[row[key]] = '';
    }
    // Handle inputId at row level (for focused exam, etc.)
    if (row.inputId && !Object.prototype.hasOwnProperty.call(initial, row.inputId)) {
      initial[row.inputId] = '';
    }
    if (row.promptCheckboxes && Array.isArray(row.promptCheckboxes)) {
      row.promptCheckboxes.forEach((item) => {
        if (item.noteId && !Object.prototype.hasOwnProperty.call(initial, item.noteId)) {
          initial[item.noteId] = '';
        }
      });
    }
    if (row.items && Array.isArray(row.items)) {
      row.items.forEach((item) => {
        if (item.inputId && !Object.prototype.hasOwnProperty.call(initial, item.inputId)) {
          initial[item.inputId] = '';
        }
        if (item.selectId && !Object.prototype.hasOwnProperty.call(initial, item.selectId)) {
          initial[item.selectId] = '';
        }
        if (item.customInputId && !Object.prototype.hasOwnProperty.call(initial, item.customInputId)) {
          initial[item.customInputId] = '';
        }
      });
    }
  });
  return initial;
};

const buildCheckboxStateMap = (rows) => {
  const initial = {};
  rows.forEach((row) => {
    if (row.type === 'checkboxRow' || row.type === 'threeColumnCheckboxRow') {
      row.items.forEach((item) => {
        initial[item.id] = false;
      });
    }
    if (row.checkboxId) {
      initial[row.checkboxId] = false;
    }
    if (row.promptCheckboxes && Array.isArray(row.promptCheckboxes)) {
      row.promptCheckboxes.forEach((item) => {
        initial[item.id] = false;
      });
    }
  });
  return initial;
};

const NREMTManualAssessment = ({ formType: initialFormType = 'unresponsive' }) => {
  // Allow dynamic form type switching when loading scenarios
  const [currentFormType, setCurrentFormType] = useState(initialFormType);
  
  // Ref to hold pending form data when switching form types
  const pendingFormDataRef = React.useRef(null);
  
  // Select layout based on current form type
  const layout = useMemo(() => 
    currentFormType === 'responsive' ? nremtResponsiveLayout : nremtUnresponsiveLayout,
    [currentFormType]
  );

  const [scripts, setScripts] = useState(() => buildInitialStateMap(layout.rows, 'promptId'));
  const [scores, setScores] = useState(() => buildInitialStateMap(layout.rows, 'scoreId'));
  const [studentInputs, setStudentInputs] = useState(() => buildInitialStateMap(layout.rows, 'studentInputId'));
  const [checkboxes, setCheckboxes] = useState(() => buildCheckboxStateMap(layout.rows));
  const [instructorSignature, setInstructorSignature] = useState('');
  const [instructorDate, setInstructorDate] = useState('');
  
  // Scenario management state
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [scenarioTitle, setScenarioTitle] = useState('');
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Reset state when form type changes, then apply pending data if any
  useEffect(() => {
    // Build fresh state for the new layout
    const freshScripts = buildInitialStateMap(layout.rows, 'promptId');
    const freshScores = buildInitialStateMap(layout.rows, 'scoreId');
    const freshStudentInputs = buildInitialStateMap(layout.rows, 'studentInputId');
    const freshCheckboxes = buildCheckboxStateMap(layout.rows);
    
    // Check if we have pending form data to apply
    if (pendingFormDataRef.current) {
      const formData = pendingFormDataRef.current;
      pendingFormDataRef.current = null; // Clear the ref
      
      // Apply pending data on top of fresh state
      setScripts(formData.scripts ? { ...freshScripts, ...formData.scripts } : freshScripts);
      setScores(formData.scores ? { ...freshScores, ...formData.scores } : freshScores);
      setStudentInputs(formData.studentInputs ? { ...freshStudentInputs, ...formData.studentInputs } : freshStudentInputs);
      setCheckboxes(formData.checkboxes ? { ...freshCheckboxes, ...formData.checkboxes } : freshCheckboxes);
      setInstructorSignature(formData.instructorSignature || '');
      setInstructorDate(formData.instructorDate || '');
      setScenarioTitle(formData.scenarioTitle || '');
      
      // Resize textareas after data loads
      requestAnimationFrame(() => {
        document.querySelectorAll('.nremt-auto-resize').forEach((element) => {
          if (element) {
            element.style.height = 'auto';
            const computed = window.getComputedStyle(element);
            const lineHeight = parseFloat(computed.lineHeight) || 14;
            const borderTop = parseFloat(computed.borderTopWidth) || 0;
            const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
            const minHeight = lineHeight + borderTop + borderBottom + 2;
            element.style.height = `${Math.max(element.scrollHeight, minHeight)}px`;
          }
        });
      });
    } else {
      // No pending data, just reset to fresh state
      setScripts(freshScripts);
      setScores(freshScores);
      setStudentInputs(freshStudentInputs);
      setCheckboxes(freshCheckboxes);
      setInstructorSignature('');
      setInstructorDate('');
      setScenarioTitle('');
    }
  }, [layout]);

  const autoResize = (element) => {
    if (!element) {
      return;
    }
    element.style.height = 'auto';
    const computed = window.getComputedStyle(element);
    const lineHeight = parseFloat(computed.lineHeight) || 14;
    const borderTop = parseFloat(computed.borderTopWidth) || 0;
    const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
    const minHeight = lineHeight + borderTop + borderBottom + 2;
    element.style.height = `${Math.max(element.scrollHeight, minHeight)}px`;
  };

  useEffect(() => {
    document.querySelectorAll('.nremt-auto-resize').forEach((element) => {
      autoResize(element);
    });
  }, []);

  const handleReset = () => {
    if (!window.confirm('Reset all fields?')) {
      return;
    }

    setScripts(buildInitialStateMap(layout.rows, 'promptId'));
    setScores(buildInitialStateMap(layout.rows, 'scoreId'));
    setStudentInputs(buildInitialStateMap(layout.rows, 'studentInputId'));
    setCheckboxes(buildCheckboxStateMap(layout.rows));
    setInstructorSignature('');
    setInstructorDate('');
    setScenarioTitle('');

    requestAnimationFrame(() => {
      document.querySelectorAll('.nremt-auto-resize').forEach((element) => {
        autoResize(element);
      });
    });
  };

  // Load form data from an object (used by both file load and database load)
  const loadFormData = (formData) => {
    if (formData.scripts) setScripts(prev => ({ ...prev, ...formData.scripts }));
    if (formData.scores) setScores(prev => ({ ...prev, ...formData.scores }));
    if (formData.studentInputs) setStudentInputs(prev => ({ ...prev, ...formData.studentInputs }));
    if (formData.checkboxes) setCheckboxes(prev => ({ ...prev, ...formData.checkboxes }));
    if (formData.instructorSignature) setInstructorSignature(formData.instructorSignature);
    if (formData.instructorDate) setInstructorDate(formData.instructorDate);
    if (formData.scenarioTitle) setScenarioTitle(formData.scenarioTitle);
    
    requestAnimationFrame(() => {
      document.querySelectorAll('.nremt-auto-resize').forEach((element) => {
        autoResize(element);
      });
    });
  };

  // Load a sample scenario from file (with form type switching)
  const loadSampleScenario = async (scenarioFile, scenarioFormType) => {
    setIsLoading(true);
    try {
      const response = await fetch(scenarioFile);
      const formData = await response.json();
      
      // Switch form type if needed before loading data
      if (scenarioFormType && scenarioFormType !== currentFormType) {
        // Store form data in ref so it gets applied after layout change
        pendingFormDataRef.current = formData;
        setCurrentFormType(scenarioFormType);
        setShowScenarioModal(false);
        alert('Scenario loaded successfully!');
        setIsLoading(false);
        return;
      }
      
      loadFormData(formData);
      setShowScenarioModal(false);
      alert('Scenario loaded successfully!');
    } catch (err) {
      console.error('Error loading scenario:', err);
      alert('Error loading scenario.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch saved scenarios from Firestore (fetch from both collections)
  const fetchSavedScenarios = async () => {
    setIsLoading(true);
    try {
      // Fetch from both collections
      const unresponsiveQuery = query(collection(db, 'nremtScenarios'), orderBy('savedAt', 'desc'));
      const responsiveQuery = query(collection(db, 'nremtResponsiveScenarios'), orderBy('savedAt', 'desc'));
      
      const [unresponsiveSnapshot, responsiveSnapshot] = await Promise.all([
        getDocs(unresponsiveQuery),
        getDocs(responsiveQuery)
      ]);
      
      const unresponsiveScenarios = unresponsiveSnapshot.docs.map(doc => ({
        id: doc.id,
        formType: 'unresponsive',
        ...doc.data()
      }));
      
      const responsiveScenarios = responsiveSnapshot.docs.map(doc => ({
        id: doc.id,
        formType: 'responsive',
        ...doc.data()
      }));
      
      // Combine and sort by date
      const allScenarios = [...unresponsiveScenarios, ...responsiveScenarios].sort(
        (a, b) => new Date(b.savedAt) - new Date(a.savedAt)
      );
      
      setSavedScenarios(allScenarios);
    } catch (err) {
      console.error('Error fetching scenarios:', err);
      alert('Error loading saved scenarios from database.');
    } finally {
      setIsLoading(false);
    }
  };

  // Save to Firestore database (saves to collection based on current form type)
  const handleSaveToDatabase = async () => {
    if (!scenarioTitle.trim()) {
      alert('Please enter a scenario title before saving.');
      return;
    }
    
    const collectionName = currentFormType === 'responsive' ? 'nremtResponsiveScenarios' : 'nremtScenarios';
    
    setIsLoading(true);
    try {
      const formData = {
        scenarioTitle: scenarioTitle.trim(),
        formType: currentFormType,
        scripts,
        scores,
        studentInputs,
        checkboxes,
        instructorSignature,
        instructorDate,
        savedAt: new Date().toISOString()
      };
      
      await addDoc(collection(db, collectionName), formData);
      alert('Scenario saved to database!');
      await fetchSavedScenarios();
    } catch (err) {
      console.error('Error saving to database:', err);
      alert('Error saving scenario to database.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load from Firestore database (with form type switching)
  const handleLoadFromDatabase = (scenario) => {
    // Switch form type if needed
    if (scenario.formType && scenario.formType !== currentFormType) {
      // Store form data in ref so it gets applied after layout change
      pendingFormDataRef.current = scenario;
      setCurrentFormType(scenario.formType);
      setShowScenarioModal(false);
      alert(`Loaded: ${scenario.scenarioTitle}`);
      return;
    }
    loadFormData(scenario);
    setShowScenarioModal(false);
    alert(`Loaded: ${scenario.scenarioTitle}`);
  };

  // Delete from Firestore database
  const handleDeleteFromDatabase = async (scenarioId, scenarioTitle, scenarioFormType) => {
    if (!window.confirm(`Delete "${scenarioTitle}"?`)) {
      return;
    }
    
    const collectionName = scenarioFormType === 'responsive' ? 'nremtResponsiveScenarios' : 'nremtScenarios';
    
    setIsLoading(true);
    try {
      await deleteDoc(doc(db, collectionName, scenarioId));
      await fetchSavedScenarios();
      alert('Scenario deleted.');
    } catch (err) {
      console.error('Error deleting scenario:', err);
      alert('Error deleting scenario from database.');
    } finally {
      setIsLoading(false);
    }
  };

  // Open scenario modal and fetch saved scenarios
  const openScenarioModal = async () => {
    setShowScenarioModal(true);
    await fetchSavedScenarios();
  };

  // Export to JSON file (keep existing functionality)
  const handleExportToFile = () => {
    const formData = {
      scenarioTitle: scenarioTitle || 'Untitled Scenario',
      formType: currentFormType,
      scripts,
      scores,
      studentInputs,
      checkboxes,
      instructorSignature,
      instructorDate,
      savedAt: new Date().toISOString()
    };
    const dataStr = JSON.stringify(formData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `nremt-${scenarioTitle || 'assessment'}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Import from JSON file (keep existing functionality)
  const handleImportFromFile = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const formData = JSON.parse(e.target.result);
        loadFormData(formData);
        alert('Form loaded successfully!');
      } catch (err) {
        alert('Error loading file. Please ensure it is a valid JSON file.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleExport = () => {
    const letterPage = document.querySelector('.nremt-letter-page');

    if (!letterPage) {
      return;
    }

    const clonedPage = letterPage.cloneNode(true);

    clonedPage.querySelectorAll('textarea').forEach((textarea) => {
      const textBlock = document.createElement('div');
      textBlock.className = 'nremt-export-field';
      textBlock.textContent = textarea.value || '';
      textBlock.style.whiteSpace = 'pre-wrap';
      textarea.replaceWith(textBlock);
    });

    clonedPage.querySelectorAll('input').forEach((input) => {
      if (input.type === 'checkbox') {
        const checkboxBlock = document.createElement('div');
        checkboxBlock.className = 'nremt-checkbox-export';
        checkboxBlock.textContent = input.checked ? '☑' : '☐';
        input.replaceWith(checkboxBlock);
        return;
      }

      const textBlock = document.createElement('div');
      textBlock.className = 'nremt-export-field';
      if (input.classList.contains('nremt-signature-input')) {
        textBlock.classList.add('nremt-export-signature');
      }
      textBlock.textContent = input.value || '';
      textBlock.style.whiteSpace = 'pre-wrap';
      input.replaceWith(textBlock);
    });

    const exportStyles = `
      body {
        margin: 0.3in;
        font-family: 'Poppins', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 10.4pt;
        line-height: 1.18;
        color: #000000;
        background: #ffffff;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        font-size: 9.8pt;
        margin: 0;
      }

      th,
      td {
        border: 1px solid #000000;
        padding: 0;
        vertical-align: middle;
        word-break: break-word;
        line-height: 1.05;
        text-align: center;
      }

      th {
        font-weight: 600;
        text-align: center;
        background: #f5f5f5;
      }

      thead th {
        text-transform: uppercase;
        font-size: 9.3pt;
        letter-spacing: 0.015em;
        padding: 0.02in 0.03in;
      }

      .nremt-major-heading th {
        background: #000000;
        color: #ffffff;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        font-size: 10pt;
        padding: 0.04in 0;
        font-weight: 600;
        text-align: center;
      }

      .sub-heading td {
        background: #ededed;
        font-weight: 600;
        text-transform: uppercase;
        font-size: 9.6pt;
        padding: 0.025in 0.06in;
      }

      .note-row td {
        font-style: italic;
        background: #fbfbfb;
        padding: 0.02in 0.05in;
      }

      .nremt-note-checkboxes {
        display: flex;
        flex-wrap: wrap;
        gap: 0.06in;
        align-items: center;
        justify-content: flex-start;
        font-style: normal;
      }

      .nremt-note-checkbox-item {
        display: flex;
        align-items: center;
        gap: 0.03in;
        font-style: normal;
      }

      col.nremt-col-lead { width: 20%; }
      col.nremt-col-objectives,
      col.nremt-col-prompt { width: 36%; }
      col.nremt-col-score { width: 8%; }

      .nremt-objective-cell {
        padding: 0.03in 0.04in;
        font-size: 9.5pt;
        text-align: left;
      }

      .nremt-objective-cell--center {
        text-align: center;
      }

      .nremt-objective-cell--center .nremt-checkbox-single {
        justify-content: center;
      }

      .nremt-objective-cell--center .nremt-checkbox-grid {
        justify-content: center;
        align-items: center;
      }

      .nremt-objective-cell--center .nremt-exclusive-options {
        justify-content: center;
      }

      .nremt-objective-cell--center .nremt-exclusive-option {
        justify-content: center;
      }

      .nremt-objective-cell--center .nremt-checkbox-single {
        justify-content: center;
      }

      .nremt-objective-cell--merged {
        vertical-align: middle;
        text-align: center;
      }

      .nremt-merged-cell {
        display: flex;
        flex-direction: column;
        gap: 0.04in;
        align-items: center;
        justify-content: center;
        text-align: center;
        min-height: 100%;
      }

      .nremt-merged-prompt {
        width: 100%;
        display: flex;
        justify-content: center;
      }

      .nremt-merged-prompt .nremt-export-field {
        text-align: center;
      }

      .nremt-note-checkboxes--center {
        justify-content: center;
        text-align: center;
      }

      .nremt-note-checkboxes--center .nremt-note-checkbox-item {
        justify-content: center;
      }

      .nremt-note-checkboxes--center .nremt-note-checkbox-item span {
        text-align: center;
      }

      .nremt-objective-exclusive {
        display: flex;
        flex-direction: column;
        gap: 0.02in;
      }

      .nremt-objective-text {
        font-weight: 500;
      }

      .nremt-objective-text--italic {
        font-style: italic;
        font-weight: 500;
      }

      .nremt-objective-text--small {
        font-size: 9pt;
        font-weight: 500;
      }

      .nremt-objective-text--tiny {
        font-size: 8pt;
        font-weight: 500;
      }

      .nremt-objective-inline {
        display: inline-block;
      }

      .nremt-exclusive-options {
        display: flex;
        flex-wrap: wrap;
        gap: 0.06in;
        align-items: center;
      }

      .nremt-exclusive-option {
        display: flex;
        align-items: center;
        gap: 0.03in;
      }

      .nremt-checkbox-item input[type="checkbox"],
      .nremt-checkbox-single input[type="checkbox"],
      .nremt-exclusive-option input[type="checkbox"],
      .nremt-note-checkbox-item input[type="checkbox"],
      .nremt-prompt-letter input[type="checkbox"] {
        width: 0.16in;
        height: 0.16in;
        margin: 0;
        accent-color: #1f2937;
      }

      .nremt-checkbox-item span,
      .nremt-checkbox-single span,
      .nremt-exclusive-option span,
      .nremt-note-checkbox-item span {
        font-size: 9.3pt;
        line-height: 1.2;
      }

      .nremt-lead-cell {
        padding: 0.03in 0.04in;
        text-align: center;
        font-size: 9.3pt;
      }

      .nremt-lead-subheading {
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.015em;
        background: #ededed;
        vertical-align: middle;
      }

      .nremt-lead-subheading--ample {
        border-right: none !important;
      }

      .nremt-prompt-cell {
        padding: 0;
        text-align: left;
      }

      .nremt-prompt-text {
        display: block;
        padding: 0.03in 0.04in;
        background: #ededed;
      }

      .nremt-score-cell {
        text-align: center;
        padding: 0.015in 0.02in;
      }

      .nremt-checkbox-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 0.01in 0.02in;
        align-items: center;
        justify-content: flex-start;
      }

      .nremt-letter-input-cell {
        padding: 0;
      }

      .nremt-letter-input {
        display: flex;
        align-items: center;
        gap: 0.04in;
        height: 100%;
        width: 100%;
      }

      .nremt-letter-input-checkbox {
        display: flex;
        align-items: center;
        gap: 0.03in;
        margin-right: 0;
        background: #ededed;
        padding: 0 0.03in;
        border-radius: 0;
        height: 100%;
        box-sizing: border-box;
      }

      .nremt-letter-input-label {
        font-weight: 600;
        letter-spacing: 0.015em;
        min-width: 0.12in;
      }

      .nremt-letter-input-field {
        flex: 1 1 auto;
        min-height: 0.26in;
        padding: 0.02in 0.03in;
        border: 1px solid #d1d5db;
        border-radius: 0;
        font-size: 9.3pt;
        line-height: 1.2;
        background: transparent;
      }

      .nremt-letter-subheading {
        background: #ededed;
      }

      .nremt-objective-borderless {
        border-left: none !important;
      }

      .nremt-no-border-bottom {
        border-bottom: none !important;
      }

      .nremt-letter-input-row td {
        padding-top: 0;
        padding-bottom: 0;
        vertical-align: middle;
      }

      .nremt-prompt-letter-grid {
        display: flex;
        flex-direction: column;
        gap: 0;
        padding: 0;
      }

      .nremt-prompt-letter-row {
        display: flex;
        align-items: center;
        gap: 0;
        height: 100%;
        width: 100%;
      }

      .nremt-prompt-letter {
        display: flex;
        align-items: center;
        gap: 0.03in;
        margin-right: 0;
        background: #ededed;
        padding: 0 0.03in;
        border-radius: 0;
        height: auto;
        box-sizing: border-box;
      }

      .nremt-prompt-letter-chip {
        font-weight: 600;
        letter-spacing: 0.015em;
        min-width: 0.12in;
      }

      .nremt-prompt-letter-field {
        flex: 1 1 auto;
        min-height: 0.26in;
        padding: 0.02in 0.03in;
        border: 1px solid #d1d5db;
        border-radius: 0;
        background: transparent;
        font-size: 9.3pt;
        line-height: 1.2;
      }

      .nremt-letter-placeholder {
        display: block;
        min-height: 0.26in;
        width: 100%;
        visibility: hidden;
      }

      .nremt-checkbox-prefix {
        display: flex;
        align-items: center;
        font-weight: 600;
        letter-spacing: 0.015em;
        margin-right: 0.08in;
      }

      .nremt-checkbox-item-wrapper {
        display: flex;
        align-items: center;
        gap: 0.02in;
      }

      .nremt-checkbox-item-wrapper--with-input {
        flex: 1 1 calc(25% - 0.04in);
        min-width: 1.6in;
      }

      .nremt-checkbox-item {
        display: flex;
        align-items: center;
        gap: 0.03in;
        padding: 0.005in 0.012in;
        white-space: nowrap;
      }

      .nremt-checkbox-item-label {
        font-size: 9.3pt;
        line-height: 1.2;
      }

      .nremt-checkbox-item-input {
        flex: 1 1 auto;
        min-height: 0.28in;
        padding: 0.02in 0.03in;
        border: 1px solid #d1d5db;
        border-radius: 0;
        resize: none;
        background: transparent;
        font-size: 9.3pt;
        line-height: 1.2;
        color: #000000;
        white-space: pre-wrap;
        word-wrap: break-word;
      }

      .nremt-checkbox-item-select {
        flex: 1 1 auto;
        min-height: 0.28in;
        min-width: 0.8in;
        padding: 0.02in 0.03in;
        border: 1px solid #d1d5db;
        border-radius: 0;
        background: transparent;
        font-size: 9.3pt;
        line-height: 1.2;
        color: #000000;
      }

      .nremt-checkbox-item-input,
      .nremt-checkbox-item-label {
        display: block;
      }

      .nremt-checkbox-split-row {
        display: grid;
        grid-template-columns: 0.35in 1fr;
        gap: 0.06in;
        align-items: flex-start;
        width: 100%;
        padding: 0.01in 0;
      }

      .nremt-checkbox-split-lead {
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 9.5pt;
      }

      .nremt-checkbox-split-body {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 0.04in;
      }

      .nremt-checkbox-split-body .nremt-checkbox-item {
        align-items: center;
        padding: 0;
      }

      .nremt-checkbox-split-body .nremt-checkbox-item-input {
        flex: 1 1 100%;
      }

      .nremt-checkbox-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 0.01in 0.02in;
        align-items: center;
        justify-content: flex-start;
      }

      .nremt-checkbox-prefix {
        display: flex;
        align-items: center;
        font-weight: 600;
        letter-spacing: 0.015em;
        margin-right: 0.08in;
      }

      .nremt-export-field {
        width: 100%;
        min-height: 0.18in;
        padding: 0.015in 0.03in;
        line-height: 1.15;
        font-size: 9.3pt;
        background: transparent;
        box-sizing: border-box;
      }

      .nremt-export-signature {
        border-bottom: 1px solid #111111;
        min-height: 0.24in;
      }
    `;

    const exportDocument = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>NREMT Manual Assessment</title><style>${exportStyles}</style></head><body>${clonedPage.innerHTML}</body></html>`;

    const blob = new Blob([exportDocument], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'nremt-manual-assessment.html';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleScriptInput = (id) => (event) => {
    autoResize(event.target);
    const { value } = event.target;
    setScripts((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  const handleStudentInput = (id) => (event) => {
    const { value } = event.target;
    setStudentInputs((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  const handleScoreInput = (id) => (event) => {
    const { value } = event.target;
    setScores((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  const handleCheckboxToggle = (id) => (event) => {
    const { checked } = event.target;
    setCheckboxes((prev) => ({
      ...prev,
      [id]: checked
    }));
  };

  const handleExclusiveOptionToggle = (groupIds, toggledId) => (event) => {
    const { checked } = event.target;
    setCheckboxes((prev) => {
      const next = { ...prev };
      groupIds.forEach((groupId) => {
        next[groupId] = false;
      });
      if (checked) {
        next[toggledId] = true;
      }
      return next;
    });
  };

  const renderLeadCell = (row, options = {}) => {
    if (options && options.skip) {
      return null;
    }
    const contentProvided = Object.prototype.hasOwnProperty.call(options, 'content');
    const content = contentProvided
      ? options.content
      : (row && Object.prototype.hasOwnProperty.call(row, 'leadContent') ? row.leadContent : '');
    const classNames = ['nremt-lead-cell'];
    if (options.className) {
      classNames.push(options.className);
    }
    if (content === '' || content === null || typeof content === 'undefined') {
      classNames.push('nremt-empty-cell');
    }
    const cellProps = {};
    if (options && typeof options.rowSpan === 'number' && options.rowSpan > 1) {
      cellProps.rowSpan = options.rowSpan;
    }
    return <td className={classNames.join(' ')} {...cellProps}>{content ?? ''}</td>;
  };

  const renderPromptField = (row) => {
    if (!row) {
      return null;
    }

    if (row.promptText) {
      const promptTextClasses = ['nremt-prompt-text'];
      if (row.promptTextStyle) {
        promptTextClasses.push(...buildTextStyleClasses(row.promptTextStyle));
      }
      return <span className={promptTextClasses.join(' ')}>{row.promptText}</span>;
    }

    if (row.promptCheckboxes && Array.isArray(row.promptCheckboxes) && row.promptCheckboxes.length > 0) {
      return (
        <div className="nremt-prompt-letter-grid">
          {row.promptCheckboxes.map((item) => (
            <div key={item.id} className="nremt-prompt-letter-row">
              <label className="nremt-prompt-letter">
                <input
                  type="checkbox"
                  checked={!!checkboxes[item.id]}
                  onChange={handleCheckboxToggle(item.id)}
                />
                <span className="nremt-prompt-letter-chip">{item.letter}</span>
              </label>
              {(() => {
                const noteId = item.noteId || row.promptId;
                if (!noteId) {
                  return null;
                }
                return (
                  <textarea
                    rows={1}
                    className="nremt-prompt-letter-field"
                    value={scripts[noteId] || ''}
                    onInput={handleScriptInput(noteId)}
                    aria-label={`${item.letter} notes`}
                  />
                );
              })()}
            </div>
          ))}
        </div>
      );
    }

    if (!row.promptId) {
      return null;
    }

    return (
      <textarea
        rows={1}
        className="script-field nremt-auto-resize"
        value={scripts[row.promptId] || ''}
        onInput={handleScriptInput(row.promptId)}
      />
    );
  };

  const renderPromptCell = (row) => {
    const promptField = renderPromptField(row);

    if (!promptField) {
      return <td className="nremt-empty-cell" />;
    }

    return (
      <td className="nremt-prompt-cell">
        {promptField}
      </td>
    );
  };

  const renderScoreCell = (row) => {
    if (!row.scoreId) {
      return <td className="nremt-empty-cell" />;
    }

    return (
      <td className="nremt-score-cell">
        <input
          type="text"
          className="nremt-score-input"
          value={scores[row.scoreId] || ''}
          onChange={handleScoreInput(row.scoreId)}
        />
      </td>
    );
  };

  const buildTextStyleClasses = (textStyle, baseClass = '') => {
    const classNames = [];
    if (baseClass) {
      classNames.push(baseClass);
    }

    if (!textStyle) {
      return classNames;
    }

    const tokens = Array.isArray(textStyle)
      ? textStyle
      : `${textStyle}`.split(/\s+/);

    tokens.forEach((token) => {
      if (!token) {
        return;
      }
      switch (token) {
        case 'italic':
          classNames.push('nremt-objective-text--italic');
          break;
        case 'small':
          classNames.push('nremt-objective-text--small');
          break;
        case 'tiny':
          classNames.push('nremt-objective-text--tiny');
          break;
        default:
          break;
      }
    });

    return [...new Set(classNames)];
  };

  const renderCheckboxContent = (row) => (
    <div className="nremt-checkbox-grid">
      {row.prefix && !row.leadContent && (
        <div className="nremt-checkbox-prefix">{row.prefix}</div>
      )}
      {row.items.map((item) => {
        const labelClasses = ['nremt-checkbox-item-label'];
        if (item.textStyle) {
          labelClasses.push(...buildTextStyleClasses(item.textStyle));
        }

        const hasInput = Boolean(item.inputId);
        const hasSelect = Boolean(item.selectId);
        const hasCustomInput = Boolean(item.customInputId);
        const isCustomSelected = hasCustomInput && studentInputs[item.selectId] === 'Custom';
        const wrapperClasses = ['nremt-checkbox-item-wrapper'];
        if (hasInput || hasSelect) {
          wrapperClasses.push('nremt-checkbox-item-wrapper--with-input');
        }

        return (
          <div key={item.id} className={wrapperClasses.join(' ')}>
            <label className="nremt-checkbox-item">
              <input
                type="checkbox"
                checked={!!checkboxes[item.id]}
                onChange={handleCheckboxToggle(item.id)}
              />
              <span className={labelClasses.join(' ')}>{item.text}</span>
            </label>
            {hasInput && (
              <input
                type="text"
                className="nremt-checkbox-item-input"
                value={studentInputs[item.inputId] || ''}
                onChange={handleStudentInput(item.inputId)}
              />
            )}
            {hasSelect && (
              <select
                className="nremt-checkbox-item-select"
                value={studentInputs[item.selectId] || ''}
                onChange={handleStudentInput(item.selectId)}
              >
                {item.options.map((option) => (
                  <option key={option} value={option}>{option || ''}</option>
                ))}
              </select>
            )}
            {isCustomSelected && (
              <input
                type="text"
                className="nremt-checkbox-item-input nremt-custom-input"
                placeholder="Enter custom finding..."
                value={studentInputs[item.customInputId] || ''}
                onChange={handleStudentInput(item.customInputId)}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  const renderObjectiveContent = (row) => {
    if (row.exclusiveOptions && Array.isArray(row.exclusiveOptions) && row.exclusiveOptions.length > 0) {
      const groupIds = row.exclusiveOptions.map((option) => option.id);
      const textClassNames = buildTextStyleClasses(row.textStyle, 'nremt-objective-text');
      return (
        <div className="nremt-objective-exclusive">
          <div className={textClassNames.join(' ')}>
            {row.text}
          </div>
          <div className="nremt-exclusive-options">
            {row.exclusiveOptions.map((option) => (
              <label key={option.id} className="nremt-exclusive-option">
                <input
                  type="checkbox"
                  checked={!!checkboxes[option.id]}
                  onChange={handleExclusiveOptionToggle(groupIds, option.id)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    // Handle objective with checkbox AND input (for focused exam, etc.)
    if (row.checkboxId && row.inputId) {
      const labelTextClasses = row.textStyle ? buildTextStyleClasses(row.textStyle) : [];
      return (
        <div className="nremt-objective-with-input">
          <label className="nremt-checkbox-single">
            <input
              type="checkbox"
              checked={!!checkboxes[row.checkboxId]}
              onChange={handleCheckboxToggle(row.checkboxId)}
            />
            <span className={labelTextClasses.length ? labelTextClasses.join(' ') : undefined}>{row.text}</span>
          </label>
          <input
            type="text"
            className="nremt-objective-input-field"
            placeholder="Findings..."
            value={studentInputs[row.inputId] || ''}
            onChange={handleStudentInput(row.inputId)}
          />
        </div>
      );
    }

    if (row.checkboxId) {
      const labelTextClasses = row.textStyle ? buildTextStyleClasses(row.textStyle) : [];
      return (
        <label className="nremt-checkbox-single">
          <input
            type="checkbox"
            checked={!!checkboxes[row.checkboxId]}
            onChange={handleCheckboxToggle(row.checkboxId)}
          />
          <span className={labelTextClasses.length ? labelTextClasses.join(' ') : undefined}>{row.text}</span>
        </label>
      );
    }

    if (row.textStyle) {
      const inlineClassNames = buildTextStyleClasses(row.textStyle, 'nremt-objective-inline');
      return <span className={inlineClassNames.join(' ')}>{row.text}</span>;
    }

    return row.text;
  };

  const renderObjectiveRow = (row, keySuffix = '', leadCellOptions = null) => {
    const promptField = renderPromptField(row);
    const shouldMergeWithPrompt = Boolean(row.mergeObjectiveWithPrompt);
    const includePromptInMergedCell = Boolean(promptField);
    const objectiveCellClasses = ['nremt-objective-cell'];
    if (row.className) {
      objectiveCellClasses.push(row.className);
    }
    if (row.objectiveAlign === 'center') {
      objectiveCellClasses.push('nremt-objective-cell--center');
    }
    if (shouldMergeWithPrompt) {
      objectiveCellClasses.push('nremt-objective-cell--merged');
    }

    const objectiveContent = renderObjectiveContent(row);
    const mergedContent = shouldMergeWithPrompt ? (
      <div className="nremt-merged-cell">
        <div>{objectiveContent}</div>
        {includePromptInMergedCell ? (
          <div className="nremt-merged-prompt">{promptField}</div>
        ) : null}
      </div>
    ) : objectiveContent;

    if (row.mergeLeadIntoObjective) {
      const colSpan = shouldMergeWithPrompt ? 3 : 2;
      return (
        <tr key={`${row.id}${keySuffix}`}>
          <td className={objectiveCellClasses.join(' ')} colSpan={colSpan}>{mergedContent}</td>
          {shouldMergeWithPrompt ? null : renderPromptCell(row)}
          {renderScoreCell(row)}
        </tr>
      );
    }

    return (
      <tr key={`${row.id}${keySuffix}`}>
        {leadCellOptions ? renderLeadCell(row, leadCellOptions) : renderLeadCell(row)}
        <td className={objectiveCellClasses.join(' ')} colSpan={shouldMergeWithPrompt ? 2 : 1}>{mergedContent}</td>
        {shouldMergeWithPrompt ? null : renderPromptCell(row)}
        {renderScoreCell(row)}
      </tr>
    );
  };

  const renderCheckboxRow = (row, leadCellOptions = null) => {
    const promptField = renderPromptField(row);
    const shouldMergeWithPrompt = Boolean(row.mergeObjectiveWithPrompt);
    const includePromptInMergedCell = Boolean(promptField);
    const objectiveCellClasses = ['nremt-objective-cell'];
    if (row.className) {
      objectiveCellClasses.push(row.className);
    }
    if (row.objectiveAlign === 'center') {
      objectiveCellClasses.push('nremt-objective-cell--center');
    }
    if (shouldMergeWithPrompt) {
      objectiveCellClasses.push('nremt-objective-cell--merged');
    }

    const checkboxContent = renderCheckboxContent(row);
    const mergedContent = shouldMergeWithPrompt ? (
      <div className="nremt-merged-cell">
        <div>{checkboxContent}</div>
        {includePromptInMergedCell ? (
          <div className="nremt-merged-prompt">{promptField}</div>
        ) : null}
      </div>
    ) : checkboxContent;

    if (row.mergeLeadIntoObjective) {
      const colSpan = shouldMergeWithPrompt ? 3 : 2;
      return (
        <tr key={row.id}>
          <td className={objectiveCellClasses.join(' ')} colSpan={colSpan}>{mergedContent}</td>
          {shouldMergeWithPrompt ? null : renderPromptCell(row)}
          {renderScoreCell(row)}
        </tr>
      );
    }

    return (
      <tr key={row.id}>
        {leadCellOptions ? renderLeadCell(row, leadCellOptions) : renderLeadCell(row)}
        <td className={objectiveCellClasses.join(' ')} colSpan={shouldMergeWithPrompt ? 2 : 1}>{mergedContent}</td>
        {shouldMergeWithPrompt ? null : renderPromptCell(row)}
        {renderScoreCell(row)}
      </tr>
    );
  };

  // Renders a row with 3 separate columns for checkbox items (Head, Neck rows)
  const renderThreeColumnCheckboxRow = (row) => {
    const renderSingleItem = (item) => {
      const labelClasses = ['nremt-checkbox-item-label'];
      if (item.textStyle) {
        labelClasses.push(...buildTextStyleClasses(item.textStyle));
      }

      const hasInput = Boolean(item.inputId);
      const hasSelect = Boolean(item.selectId);
      const hasCustomInput = Boolean(item.customInputId);
      const isCustomSelected = hasCustomInput && studentInputs[item.selectId] === 'Custom';

      return (
        <div className="nremt-three-col-item">
          <label className="nremt-checkbox-item">
            <input
              type="checkbox"
              checked={!!checkboxes[item.id]}
              onChange={handleCheckboxToggle(item.id)}
            />
            <span className={labelClasses.join(' ')}>{item.text}</span>
          </label>
          {hasSelect && (
            <select
              className="nremt-checkbox-item-select"
              value={studentInputs[item.selectId] || ''}
              onChange={handleStudentInput(item.selectId)}
            >
              {item.options.map((option) => (
                <option key={option} value={option}>{option || ''}</option>
              ))}
            </select>
          )}
          {hasInput && (
            <input
              type="text"
              className="nremt-checkbox-item-input"
              value={studentInputs[item.inputId] || ''}
              onChange={handleStudentInput(item.inputId)}
            />
          )}
          {isCustomSelected && (
            <input
              type="text"
              className="nremt-checkbox-item-input nremt-custom-input"
              placeholder="Enter custom finding..."
              value={studentInputs[item.customInputId] || ''}
              onChange={handleStudentInput(item.customInputId)}
            />
          )}
        </div>
      );
    };

    return (
      <tr key={row.id} className="nremt-three-col-row">
        <td className="nremt-lead-cell">{/* Empty lead cell */}</td>
        <td className="nremt-three-col-container" colSpan={2}>
          <div className="nremt-three-col-grid">
            {row.items.map((item) => (
              <div key={item.id} className="nremt-three-col-cell">
                {renderSingleItem(item)}
              </div>
            ))}
          </div>
        </td>
        {renderScoreCell(row)}
      </tr>
    );
  };

  const renderLetterInputRow = (row, leadCellOptions = null) => {
    const objectiveCellClasses = ['nremt-objective-cell', 'nremt-letter-input-cell'];
    if (row.className) {
      objectiveCellClasses.push(row.className);
    }
    
    // Support rowSpan for objective cell (e.g., E spanning S, T, Other rows)
    const objectiveCellProps = {};
    if (row.objectiveRowSpan) {
      objectiveCellProps.rowSpan = row.objectiveRowSpan;
      objectiveCellClasses.push('nremt-objective-cell--spanning');
    }
    
    return (
      <tr key={row.id} className="nremt-letter-input-row">
        {leadCellOptions ? renderLeadCell(row, leadCellOptions) : renderLeadCell(row)}
        <td className={objectiveCellClasses.join(' ')} {...objectiveCellProps}>
          <div className="nremt-letter-input nremt-letter-input--top-aligned">
            <label className="nremt-letter-input-checkbox">
              {row.checkboxId ? (
                <input
                  type="checkbox"
                  checked={!!checkboxes[row.checkboxId]}
                  onChange={handleCheckboxToggle(row.checkboxId)}
                />
              ) : null}
              <span className="nremt-letter-input-label">{row.text}</span>
            </label>
            {row.studentInputId ? (
              <textarea
                rows={1}
                className="nremt-letter-input-field"
                value={studentInputs[row.studentInputId] || ''}
                onInput={handleStudentInput(row.studentInputId)}
              />
            ) : null}
          </div>
        </td>
        {renderPromptCell(row)}
        {renderScoreCell(row)}
      </tr>
    );
  };

  // Renders T and Other rows - prompt cell with OPQRST content, handles subsection spanning
  const renderOpqrstOnlyRow = (row, leadCellOptions = null) => {
    const promptField = renderPromptField(row);
    
    return (
      <tr key={row.id} className="nremt-letter-input-row">
        {leadCellOptions ? renderLeadCell(row, leadCellOptions) : (
          <td className="nremt-lead-cell nremt-opqrst-empty-cell">
            {/* Empty lead cell with border */}
          </td>
        )}
        {!row.skipObjectiveCell && (
          <td className="nremt-objective-cell nremt-opqrst-empty-cell">
            {/* Empty objective cell with border */}
          </td>
        )}
        <td className="nremt-prompt-cell">
          {promptField}
        </td>
        <td className="nremt-score-cell nremt-opqrst-empty-cell">
          {/* Empty score cell with border */}
        </td>
      </tr>
    );
  };

  // Legacy promptLetterRow - kept for backwards compatibility
  const renderPromptLetterRow = (row, leadCellOptions = null) => {
    return renderOpqrstOnlyRow(row, leadCellOptions);
  };

  const renderNoteCheckboxRow = (row) => {
    const noteClassNames = ['nremt-note-checkboxes'];
    if (row.align === 'center') {
      noteClassNames.push('nremt-note-checkboxes--center');
    }

    return (
      <tr key={row.id} className="note-row">
        <td colSpan={3}>
          <div className={noteClassNames.join(' ')}>
            {row.items.map((item) => (
              <label key={item.id} className="nremt-note-checkbox-item">
                <input
                  type="checkbox"
                  checked={!!checkboxes[item.id]}
                  onChange={handleCheckboxToggle(item.id)}
                />
                <span>{item.text}</span>
              </label>
            ))}
          </div>
        </td>
        {renderScoreCell(row)}
      </tr>
    );
  };

  const buildTableRows = () => {
    const rows = [];
    const layoutRows = layout.rows;
    let currentSubsectionCell = null;

    const hasNonEmptyLeadContent = (target) => (
      target
      && Object.prototype.hasOwnProperty.call(target, 'leadContent')
      && target.leadContent !== null
      && target.leadContent !== ''
      && typeof target.leadContent !== 'undefined'
    );

    const computeSubsectionRowSpan = (startIndex) => {
      let span = 0;
      for (let index = startIndex; index < layoutRows.length; index += 1) {
        const candidate = layoutRows[index];
        if (!candidate) {
          break;
        }
        if (candidate.type === 'section' || candidate.type === 'subsection') {
          break;
        }
        if (candidate.type === 'note' || candidate.type === 'noteCheckboxRow') {
          break;
        }
        if (hasNonEmptyLeadContent(candidate)) {
          break;
        }
        if (candidate.breaksSubsection) {
          break;
        }
        if (candidate.type === 'objectiveWithNote') {
          span += candidate.noteBreaksSubsection ? 1 : 2;
        } else if (candidate.type === 'objective' || candidate.type === 'checkboxRow' || candidate.type === 'letterInputRow' || candidate.type === 'promptLetterRow' || candidate.type === 'opqrstOnlyRow') {
          span += 1;
        }
      }
      return span;
    };

    const consumeSubsectionRows = (count) => {
      if (!currentSubsectionCell) {
        return;
      }
      currentSubsectionCell.remaining -= count;
      if (currentSubsectionCell.remaining <= 0) {
        currentSubsectionCell = null;
      }
    };

    const resolveLeadCellOptions = (row, isGroupRow) => {
      if (row && row.mergeLeadIntoObjective && row.leadContent) {
        return { skip: true };
      }
      if (hasNonEmptyLeadContent(row)) {
        return { content: row.leadContent };
      }
      if (isGroupRow && currentSubsectionCell) {
        if (!currentSubsectionCell.rendered) {
          currentSubsectionCell.rendered = true;
          return {
            content: currentSubsectionCell.label,
            className: currentSubsectionCell.className || 'nremt-lead-subheading',
            rowSpan: currentSubsectionCell.span
          };
        }
        return { skip: true };
      }
      return null;
    };

    for (let i = 0; i < layoutRows.length; i += 1) {
      const row = layoutRows[i];

      if (row.type === 'section') {
        currentSubsectionCell = null;
        rows.push(
          <tr key={row.id} className="nremt-major-heading">
            <th colSpan={4} scope="colgroup">{row.title}</th>
          </tr>
        );
        continue;
      }

      if (row.type === 'subsection') {
        const rowSpan = computeSubsectionRowSpan(i + 1);
        if (rowSpan > 0) {
          currentSubsectionCell = {
            label: row.title,
            span: rowSpan,
            remaining: rowSpan,
            rendered: false,
            className: row.leadClassName || 'nremt-lead-subheading'
          };
        } else {
          currentSubsectionCell = null;
          rows.push(
            <tr key={row.id} className="sub-heading">
              <td colSpan={4}>{row.title}</td>
            </tr>
          );
        }
        continue;
      }

      if (row.type === 'note') {
        currentSubsectionCell = null;
        rows.push(
          <tr key={row.id} className="note-row">
            <td colSpan={4}>{row.text}</td>
          </tr>
        );
        continue;
      }

      if (row.type === 'noteCheckboxRow') {
        currentSubsectionCell = null;
        rows.push(renderNoteCheckboxRow(row));
        continue;
      }

      const hasLeadContent = hasNonEmptyLeadContent(row);
      if (currentSubsectionCell && row.breaksSubsection && !hasLeadContent) {
        currentSubsectionCell = null;
      }
      const isGroupRow = Boolean(currentSubsectionCell) && !hasLeadContent;
      const leadCellOptions = resolveLeadCellOptions(row, isGroupRow);

      if (row.type === 'letterInputRow') {
        rows.push(renderLetterInputRow(row, leadCellOptions));
        if (isGroupRow) {
          consumeSubsectionRows(1);
        } else if (hasLeadContent || row.mergeLeadIntoObjective) {
          currentSubsectionCell = null;
        }
        continue;
      }

      if (row.type === 'promptLetterRow') {
        rows.push(renderPromptLetterRow(row, leadCellOptions));
        if (isGroupRow) {
          consumeSubsectionRows(1);
        } else if (hasLeadContent || row.mergeLeadIntoObjective) {
          currentSubsectionCell = null;
        }
        continue;
      }

      if (row.type === 'opqrstOnlyRow') {
        rows.push(renderOpqrstOnlyRow(row, leadCellOptions));
        if (isGroupRow) {
          consumeSubsectionRows(1);
        } else if (hasLeadContent || row.mergeLeadIntoObjective) {
          currentSubsectionCell = null;
        }
        continue;
      }

      if (row.type === 'checkboxRow') {
        rows.push(renderCheckboxRow(row, leadCellOptions));
        if (isGroupRow) {
          consumeSubsectionRows(1);
        } else if (hasLeadContent || row.mergeLeadIntoObjective) {
          currentSubsectionCell = null;
        }
        continue;
      }

      if (row.type === 'threeColumnCheckboxRow') {
        rows.push(renderThreeColumnCheckboxRow(row));
        if (isGroupRow) {
          consumeSubsectionRows(1);
        } else if (hasLeadContent || row.mergeLeadIntoObjective) {
          currentSubsectionCell = null;
        }
        continue;
      }

      if (row.type === 'objectiveWithNote') {
        rows.push(renderObjectiveRow(row, '', leadCellOptions));
        const objectiveWasGrouped = isGroupRow;
        if (objectiveWasGrouped) {
          consumeSubsectionRows(1);
        } else if (hasLeadContent || row.mergeLeadIntoObjective) {
          currentSubsectionCell = null;
        }

        const noteUsesSubsection = objectiveWasGrouped && !row.noteBreaksSubsection;
        if (row.noteBreaksSubsection) {
          currentSubsectionCell = null;
        }

        const noteMergeLead = Boolean(row.noteMergeLead);
        const noteColSpan = row.noteColSpan || (noteMergeLead ? 3 : 2);
        const noteLeadOptions = noteMergeLead
          ? { skip: true }
          : noteUsesSubsection
            ? { skip: true }
            : row.noteLeadLabel
              ? { className: 'nremt-lead-subheading', content: row.noteLeadLabel }
              : { className: 'nremt-empty-cell', content: '' };

        rows.push(
          <tr key={`${row.id}-note`} className="note-row">
            {noteMergeLead ? null : renderLeadCell(row, noteLeadOptions)}
            <td colSpan={noteColSpan}>{row.note}</td>
            <td className="nremt-empty-cell" />
          </tr>
        );

        if (noteUsesSubsection) {
          consumeSubsectionRows(1);
        }
        continue;
      }

      if (row.type === 'objective') {
        rows.push(renderObjectiveRow(row, '', leadCellOptions));
        if (isGroupRow) {
          consumeSubsectionRows(1);
        } else if (hasLeadContent || row.mergeLeadIntoObjective) {
          currentSubsectionCell = null;
        }
        continue;
      }
    }

    rows.push(
      <tr key="instructor-heading" className="nremt-major-heading">
        <th colSpan={4} scope="colgroup">Instructor certification</th>
      </tr>
    );

    rows.push(
      <tr key="instructor-signature">
        {renderLeadCell(null)}
        <td className="nremt-objective-cell">Instructor signature</td>
        <td className="nremt-prompt-cell">
          <input
            type="text"
            className="nremt-signature-input"
            placeholder="Sign or print name"
            value={instructorSignature}
            onChange={(event) => setInstructorSignature(event.target.value)}
          />
        </td>
        <td className="nremt-score-cell nremt-empty-cell" />
      </tr>
    );

    rows.push(
      <tr key="instructor-date">
        {renderLeadCell(null)}
        <td className="nremt-objective-cell">Date</td>
        <td className="nremt-prompt-cell">
          <input
            type="date"
            className="nremt-signature-input"
            value={instructorDate}
            onChange={(event) => setInstructorDate(event.target.value)}
          />
        </td>
        <td className="nremt-score-cell nremt-empty-cell" />
      </tr>
    );

    return rows;
  };

  return (
    <div className="nremt-letter-screen">
      <div className="no-print nremt-control-bar">
        <input
          type="text"
          className="nremt-scenario-title-input"
          placeholder="Scenario Title..."
          value={scenarioTitle}
          onChange={(e) => setScenarioTitle(e.target.value)}
        />
        <button 
          type="button" 
          className="nremt-control-button nremt-control-button--primary" 
          onClick={handleSaveToDatabase}
          disabled={isLoading}
        >
          💾 Save to DB
        </button>
        <button 
          type="button" 
          className="nremt-control-button" 
          onClick={openScenarioModal}
          disabled={isLoading}
        >
          📂 Load Scenario
        </button>
        <button type="button" className="nremt-control-button" onClick={handleExportToFile}>
          ⬇️ Export JSON
        </button>
        <label className="nremt-control-button nremt-load-label">
          ⬆️ Import JSON
          <input type="file" accept=".json" onChange={handleImportFromFile} style={{ display: 'none' }} />
        </label>
        <button type="button" className="nremt-control-button" onClick={handleExport}>
          🌐 Export HTML
        </button>
        <button type="button" className="nremt-control-button" onClick={handlePrint}>
          🖨️ Print
        </button>
        <button type="button" className="nremt-control-button nremt-control-button--danger" onClick={handleReset}>
          🔄 Reset
        </button>
      </div>

      {/* Scenario Load Modal */}
      {showScenarioModal && (
        <div className="nremt-modal-overlay" onClick={() => setShowScenarioModal(false)}>
          <div className="nremt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nremt-modal-header">
              <h2>Load Scenario</h2>
              <button 
                className="nremt-modal-close" 
                onClick={() => setShowScenarioModal(false)}
              >
                ×
              </button>
            </div>
            <div className="nremt-modal-content">
              {isLoading && <div className="nremt-loading">Loading...</div>}
              
              <div className="nremt-scenario-section">
                <h3>📁 Sample Scenarios</h3>
                <div className="nremt-scenario-list">
                  {ALL_SAMPLE_SCENARIOS.map((scenario) => (
                    <div key={scenario.id} className="nremt-scenario-item">
                      <div className="nremt-scenario-info">
                        <span className="nremt-scenario-title">{scenario.title}</span>
                        <span className="nremt-scenario-type">
                          {scenario.formType === 'responsive' ? '🟢 Responsive' : '🔴 Unresponsive'}
                        </span>
                      </div>
                      <button
                        className="nremt-scenario-load-btn"
                        onClick={() => loadSampleScenario(scenario.file, scenario.formType)}
                        disabled={isLoading}
                      >
                        Load
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="nremt-scenario-section">
                <h3>☁️ Saved Scenarios (Database)</h3>
                <div className="nremt-scenario-list">
                  {savedScenarios.length === 0 ? (
                    <div className="nremt-scenario-empty">No saved scenarios found.</div>
                  ) : (
                    savedScenarios.map((scenario) => (
                      <div key={scenario.id} className="nremt-scenario-item">
                        <div className="nremt-scenario-info">
                          <span className="nremt-scenario-title">{scenario.scenarioTitle}</span>
                          <span className="nremt-scenario-meta">
                            <span className="nremt-scenario-type">
                              {scenario.formType === 'responsive' ? '🟢 Responsive' : '🔴 Unresponsive'}
                            </span>
                            <span className="nremt-scenario-date">
                              {new Date(scenario.savedAt).toLocaleDateString()}
                            </span>
                          </span>
                        </div>
                        <div className="nremt-scenario-actions">
                          <button
                            className="nremt-scenario-load-btn"
                            onClick={() => handleLoadFromDatabase(scenario)}
                            disabled={isLoading}
                          >
                            Load
                          </button>
                          <button
                            className="nremt-scenario-delete-btn"
                            onClick={() => handleDeleteFromDatabase(scenario.id, scenario.scenarioTitle, scenario.formType)}
                            disabled={isLoading}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="nremt-letter-wrapper">
        <div className="nremt-letter-page">
          <table className="nremt-form-table nremt-section-table">
            <colgroup>
              <col className="nremt-col-lead" />
              <col className="nremt-col-objectives" />
              <col className="nremt-col-prompt" />
              <col className="nremt-col-score" />
            </colgroup>
            <thead>
              <tr className="nremt-main-header">
                {layout.header.map((column) => (
                  <th key={column.key} colSpan={column.colSpan}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>{buildTableRows()}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NREMTManualAssessment;
